import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import Splash from "@/pages/Splash";
import Download from "@/pages/Download";
import Home from "@/pages/Home";
import Categories from "@/pages/Categories";
import CalculatorDetail from "@/pages/CalculatorDetail";
import Converters from "@/pages/Converters";
import Saved from "@/pages/Saved";
import Favorites from "@/pages/Favorites";
import FieldData from "@/pages/FieldData";
import Pricing from "@/pages/Pricing";
import Subscribe from "@/pages/Subscribe";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import SubscriptionCancel from "@/pages/SubscriptionCancel";
import EnterpriseDashboard from "@/pages/EnterpriseDashboard";
import Auth from "@/pages/Auth";
import PasswordReset from "@/pages/PasswordReset";
import Account from "@/pages/Account";
import ChurnDashboard from "@/pages/ChurnDashboard";
import FlangeFinder from "@/pages/FlangeFinder";
import DecimalFractionConverter from "@/pages/DecimalFractionConverter";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import Settings from "@/pages/Settings";
import Diagnostic from "@/pages/Diagnostic";
import WellDashboard from "@/pages/WellDashboard";
import SubseaProfile from "@/pages/SubseaProfile";
import RequireProAccess from "@/components/RequireProAccess";
import UpdateAvailableBanner from "@/components/UpdateAvailableBanner";
import Safety from "@/pages/Safety";
import Reports from "@/pages/Reports";
import DailyDrillingReport from "@/pages/DailyDrillingReport";
import DailyMudReport from "@/pages/DailyMudReport";
import TripSheet from "@/pages/TripSheet";
import BitRunRecord from "@/pages/BitRunRecord";
import CementingReport from "@/pages/CementingReport";
import BopTestSheet from "@/pages/BopTestSheet";
import HseIncidentForm from "@/pages/HseIncidentForm";
import ToolboxTalk from "@/pages/ToolboxTalk";
import PreTourBriefing from "@/pages/PreTourBriefing";
import PreJobBriefing from "@/pages/PreJobBriefing";
import JsaGenerator from "@/pages/JsaGenerator";
import DowntimeReport from "@/pages/DowntimeReport";
import EquipmentFailureReport from "@/pages/EquipmentFailureReport";
import HandoverNotes from "@/pages/HandoverNotes";
import DailyOperationsSummary from "@/pages/DailyOperationsSummary";
import LotFitReport from "@/pages/LotFitReport";
import SubseaBopTestReport from "@/pages/SubseaBopTestReport";
import SurfaceBopTestReport from "@/pages/SurfaceBopTestReport";
// Legacy kick-sheet page wrappers (KickSheet.jsx) are no longer routed.
// They will be physically deleted in a follow-up cleanup commit.
import { StackModeProvider } from "@/lib/stackModeContext";
import TermsOfService from "@/pages/legal/TermsOfService";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import Disclaimer from "@/pages/legal/Disclaimer";
import Contact from "@/pages/legal/Contact";
import Reference from "@/pages/Reference";
import ProfileHub from "@/pages/profiles/ProfileHub";
import ProfileList from "@/pages/profiles/ProfileList";
import ProfileEditor from "@/pages/profiles/ProfileEditor";
import KillSheetWizard from "@/pages/killsheet/KillSheetWizard";
import KillSheetEditor from "@/pages/killsheet/KillSheetEditor";
import KillSheetPrint from "@/pages/killsheet/KillSheetPrint";
import RigSheetHub from "@/pages/rigsheet/RigSheetHub";
import RigSheetEditor from "@/pages/rigsheet/RigSheetEditor";
import ReferenceLibrariesHub from "@/pages/reflibs/ReferenceLibrariesHub";
import ReferenceLibraryRecordEditor from "@/pages/reflibs/ReferenceLibraryRecordEditor";
import WellProfileHub from "@/pages/well/WellProfileHub";
import WellProfileEditor from "@/pages/well/WellProfileEditor";
import BhaHub from "@/pages/well/BhaHub";
import BhaEditor from "@/pages/well/BhaEditor";
import DrillStringHub from "@/pages/well/DrillStringHub";
import DrillStringEditor from "@/pages/well/DrillStringEditor";
import KillSheetOpHub from "@/pages/well/KillSheetOpHub";
import KillSheetOpEditor from "@/pages/well/KillSheetOpEditor";
import KillSheetOpPrint from "@/pages/well/KillSheetOpPrint";
import { Navigate } from "react-router-dom";
import { installConnectivityBridge } from "@/lib/connectivity";
import "@/lib/buildInfo";
import "@/lib/rce/bootstrap";
import { installNumericWheelGuard } from "@/lib/ui/numericWheelGuard";
import SessionSwitcher from "@/components/rce/SessionSwitcher";
import HowToUse from "@/pages/HowToUse";
import MigrationWizard from "@/pages/migrate/MigrationWizard";
import ContextInspector from "@/pages/dev/ContextInspector";
import InheritanceTrace from "@/pages/dev/InheritanceTrace";
import MudProgramHub from "@/pages/mud/MudProgramHub";
import MudProgramEditor from "@/pages/mud/MudProgramEditor";
import RequireAuth from "@/components/RequireAuth";
import OwnerDashboard from "@/pages/OwnerDashboard";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import { trackAppSessionStarted } from "@/lib/telemetry";

// Route-identity remount guard for canonical Kill Sheets.
// KillSheetOpEditor is mounted at both /wells/:wellId/kill-sheets/new and
// /wells/:wellId/kill-sheets/:ksId. Because both routes render the same
// element type at the same position in the tree, React reuses the same
// component instance across navigations between them instead of
// unmounting/remounting it, so event-specific in-memory state (SIDPP,
// SICP, slow-pump pressure, pump rate, influx size, etc.) from a
// previously viewed sheet can remain visible on what should be a fresh
// sheet. Keying the element on the route's Kill Sheet identity ("new" or
// the specific ksId) forces React to remount on identity change while
// leaving unsaved input alone when the identity does not change.
function KillSheetOpEditorRoute() {
  const { ksId } = useParams();
  return <KillSheetOpEditor key={ksId || "new"} />;
}

function App() {
  useEffect(() => {
    // Owner Dashboard V1 — non-sensitive, additive product-usage telemetry.
    // Fires once per browser tab session; never blocks app startup.
    trackAppSessionStarted();
    installConnectivityBridge();
    // RC-1 Data Integrity Fix — global guard that prevents accidental
    // mousewheel/touchpad-wheel modification of `<input type="number">`
    // values.  Blurs the focused numeric input on wheel; page scroll is
    // untouched.  Presentation-only.
    installNumericWheelGuard();
    // Service Worker registration + update lifecycle is now handled by the
    // <UpdateAvailableBanner /> component (mounted below). The banner uses
    // the `useServiceWorkerUpdate` hook to detect new builds, auto-reload
    // on `controllerchange`, and surface a one-tap "Update now" CTA so
    // returning users always land on the freshest bundle without manually
    // clearing browser data.
  }, []);

  return (
    <div className="App">
      <UpdateAvailableBanner />
      <StackModeProvider>
        <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Splash />} />
          <Route path="/download" element={<Download />} />
          <Route element={<Layout />}>
            {/* Public — no sign-in required */}
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/reset" element={<PasswordReset />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/contact" element={<Contact />} />

            {/* Everything else requires sign-in (Phase 1 Auth/Storage-Scoping) */}
            <Route element={<RequireAuth />}>
            <Route path="/home" element={<Home />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/categories/:catId" element={<Categories />} />
            <Route path="/categories/:catId/:stackMode" element={<Categories />} />
            <Route path="/calc/:calcId" element={<CalculatorDetail />} />
            <Route path="/tools/flange-slide-rule" element={<RequireProAccess context="Flange Slide Rule"><FlangeFinder /></RequireProAccess>} />
            <Route path="/tools/fraction-converter" element={<RequireProAccess context="Fraction Converter"><DecimalFractionConverter /></RequireProAccess>} />
            <Route path="/converters" element={<Converters />} />
            <Route path="/saved" element={<RequireProAccess context="Saved calculations"><Saved /></RequireProAccess>} />
            <Route path="/how-to-use" element={<HowToUse />} />
            <Route path="/favorites" element={<RequireProAccess context="Favorites"><Favorites /></RequireProAccess>} />
            <Route path="/library" element={<FieldData />} />
            <Route path="/subscription" element={<Navigate to="/pricing" replace />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
            <Route path="/enterprise/dashboard" element={<EnterpriseDashboard />} />
            <Route path="/reference" element={<Reference />} />
            <Route path="/profiles" element={<ProfileHub />} />
            <Route path="/profiles/:kind" element={<ProfileList />} />
            <Route path="/profiles/:kind/new" element={<ProfileEditor />} />
            <Route path="/profiles/:kind/:id" element={<ProfileEditor />} />

            <Route path="/rig-sheets"        element={<RigSheetHub />} />
            <Route path="/rig-sheets/new"    element={<RigSheetEditor />} />
            <Route path="/rig-sheets/:id"    element={<RigSheetEditor />} />

            <Route path="/reference-libraries"                 element={<RequireProAccess context="Reference Libraries"><ReferenceLibrariesHub /></RequireProAccess>} />
            <Route path="/reference-libraries/:category"       element={<RequireProAccess context="Reference Libraries"><ReferenceLibrariesHub /></RequireProAccess>} />
            <Route path="/reference-libraries/:category/new"   element={<RequireProAccess context="Reference Libraries"><ReferenceLibraryRecordEditor /></RequireProAccess>} />
            <Route path="/reference-libraries/:category/:id"   element={<RequireProAccess context="Reference Libraries"><ReferenceLibraryRecordEditor /></RequireProAccess>} />

            {/* Module 3 — Well Profile (master record) */}
            <Route path="/wells"        element={<WellProfileHub />} />
            <Route path="/wells/new"    element={<WellProfileEditor />} />
            <Route path="/wells/:id"    element={<WellProfileEditor />} />

            {/* Module 3.2a — BHA child of a well */}
            <Route path="/wells/:wellId/bha"             element={<BhaHub />} />
            <Route path="/wells/:wellId/bha/new"         element={<BhaEditor />} />
            <Route path="/wells/:wellId/bha/:bhaId"      element={<BhaEditor />} />

            {/* Module 3.2b — Drill String child of a well */}
            <Route path="/wells/:wellId/drill-strings"          element={<DrillStringHub />} />
            <Route path="/wells/:wellId/drill-strings/new"      element={<DrillStringEditor />} />
            <Route path="/wells/:wellId/drill-strings/:dsId"    element={<DrillStringEditor />} />

            {/* Module 4.1a — Kill Sheets (Operational Engineering) under a well */}
            <Route path="/wells/:wellId/kill-sheets"                element={<KillSheetOpHub />} />
            <Route path="/wells/:wellId/kill-sheets/new"            element={<KillSheetOpEditorRoute />} />
            <Route path="/wells/:wellId/kill-sheets/:ksId"          element={<KillSheetOpEditorRoute />} />
            <Route path="/wells/:wellId/kill-sheets/:ksId/print"    element={<KillSheetOpPrint />} />
            {/* New canonical IWCF kill-sheet URLs */}
            <Route path="/kill-sheet/:variant" element={<KillSheetEditor />} />
            <Route path="/kill-sheet/:variant/wizard" element={<KillSheetWizard />} />
            <Route path="/kill-sheet/print/:id" element={<KillSheetPrint />} />
            {/* P5 — 301-style redirects from legacy kick-sheet URLs */}
            <Route path="/reports/surface-kick-sheet-vertical"   element={<Navigate to="/kill-sheet/surface-vertical"   replace />} />
            <Route path="/reports/surface-kick-sheet-horizontal" element={<Navigate to="/kill-sheet/surface-horizontal" replace />} />
            <Route path="/reports/subsea-kick-sheet-vertical"    element={<Navigate to="/kill-sheet/subsea-vertical"    replace />} />
            <Route path="/reports/subsea-kick-sheet-horizontal"  element={<Navigate to="/kill-sheet/subsea-horizontal"  replace />} />
            <Route path="/account" element={<Account />} />
            <Route path="/admin/churn" element={<RequireProAccess context="Admin"><ChurnDashboard /></RequireProAccess>} />
            <Route path="/admin/owner-dashboard" element={<RouteErrorBoundary label="Owner Dashboard" backTo="/account" backLabel="Back to Account"><OwnerDashboard /></RouteErrorBoundary>} />
            <Route path="/billing/success" element={<CheckoutSuccess />} />
            <Route path="/billing/cancel" element={<CheckoutCancel />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/diagnostic" element={<Diagnostic />} />
            <Route path="/well/:wellName" element={<RequireProAccess context="Per-well dashboards"><WellDashboard /></RequireProAccess>} />
            <Route path="/subsea-profile" element={<RequireProAccess context="The subsea profile workspace"><SubseaProfile /></RequireProAccess>} />
            <Route path="/safety" element={<Safety />} />
            <Route path="/safety-center" element={<Safety />} />
            <Route path="/reports" element={<RequireProAccess context="Reports"><Reports /></RequireProAccess>} />
            <Route path="/reports/daily-drilling-report" element={<RequireProAccess context="Reports"><DailyDrillingReport /></RequireProAccess>} />
            <Route path="/reports/daily-mud-report" element={<RequireProAccess context="Reports"><DailyMudReport /></RequireProAccess>} />
            <Route path="/reports/trip-sheet" element={<RequireProAccess context="Reports"><TripSheet /></RequireProAccess>} />
            <Route path="/reports/bit-run-record" element={<RequireProAccess context="Reports"><BitRunRecord /></RequireProAccess>} />
            <Route path="/reports/cementing-report" element={<RequireProAccess context="Reports"><CementingReport /></RequireProAccess>} />
            <Route path="/reports/bop-test-sheet" element={<RequireProAccess context="Reports"><BopTestSheet /></RequireProAccess>} />
            <Route path="/reports/hse-incident-form" element={<RequireProAccess context="Reports"><HseIncidentForm /></RequireProAccess>} />
            <Route path="/reports/toolbox-talk" element={<RequireProAccess context="Reports"><ToolboxTalk /></RequireProAccess>} />
            <Route path="/reports/pre-tour-briefing" element={<RequireProAccess context="Reports"><PreTourBriefing /></RequireProAccess>} />
            <Route path="/reports/pre-job-briefing" element={<RequireProAccess context="Reports"><PreJobBriefing /></RequireProAccess>} />
            <Route path="/reports/jsa-generator" element={<RequireProAccess context="Reports"><JsaGenerator /></RequireProAccess>} />
            <Route path="/reports/downtime-report" element={<RequireProAccess context="Reports"><DowntimeReport /></RequireProAccess>} />
            <Route path="/reports/equipment-failure-report" element={<RequireProAccess context="Reports"><EquipmentFailureReport /></RequireProAccess>} />
            <Route path="/reports/handover-notes" element={<RequireProAccess context="Reports"><HandoverNotes /></RequireProAccess>} />
            <Route path="/reports/daily-operations-summary" element={<RequireProAccess context="Reports"><DailyOperationsSummary /></RequireProAccess>} />
            <Route path="/reports/lot-fit-report" element={<RequireProAccess context="Reports"><LotFitReport /></RequireProAccess>} />
            <Route path="/reports/subsea-bop-test-report" element={<RequireProAccess context="Reports"><SubseaBopTestReport /></RequireProAccess>} />
            <Route path="/reports/surface-bop-test-report" element={<RequireProAccess context="Reports"><SurfaceBopTestReport /></RequireProAccess>} />
            {/* P5 — Surface/Subsea Kick Sheets fully replaced by IWCF kill-sheet wizard at /kill-sheet/:variant */}

            {/* RigSentinel Context Engine — Phase RCE-1 */}
            <Route path="/migrate/projects" element={<MigrationWizard />} />
            <Route path="/dev/context-inspector" element={<ContextInspector />} />
            <Route path="/dev/inheritance-trace" element={<InheritanceTrace />} />

            {/* RCE-3 — Drilling Fluid Management */}
            <Route path="/wells/:wellId/mud-programs"        element={<MudProgramHub />} />
            <Route path="/wells/:wellId/mud-programs/new"    element={<MudProgramEditor />} />
            <Route path="/wells/:wellId/mud-programs/:id"    element={<MudProgramEditor />} />
            </Route>
          </Route>
        </Routes>
        {/* RCE — Phase 1: global session switcher modal (mounted once) */}
        <SessionSwitcher />
      </BrowserRouter>
      </StackModeProvider>
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "#16181D",
            color: "#F8F9FA",
            border: "1px solid #2A2E39",
            fontFamily: "IBM Plex Sans, sans-serif",
          },
        }}
      />
    </div>
  );
}

export default App;
